// src/Pages/Holidays/TravelTypeResults.js

import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Spinner } from 'react-bootstrap';
import apiService from '../../Common/Configurations/APIService';
import config from '../../Common/Configurations/APIConfig';
import './HolidayScreen.css'; // Re-use the same styles
import DefaultData from '../../CompanyData/DefaultData';

// This card can be extracted into a shared component later to avoid duplication
const PackageCard = ({ pkg }) => (
    <Card className="package-card h-100">
        <Card.Img variant="top" src={pkg.imageSrc || 'https://via.placeholder.com/400x200?text=Package'} alt={pkg.packageName} />
        <Card.Body className="d-flex flex-column">
            <Card.Title className="fw-bold mb-1">
                <Link to={`/holiday-details/${pkg.packageId}`} className="card-title-link">
                    {pkg.packageName}
                </Link>
            </Card.Title>
            <div className="icon-text mb-2 small">
                {/* Using a generic SVG icon for consistency */}
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" className="me-1"><path d="M8 16s6-5.686 6-10A6 6 0 0 0 2 6c0 4.314 6 10 6 10m0-7a3 3 0 1 1 0-6 3 3 0 0 1 0 6" /></svg>
                <span>{pkg.destinationsCover}</span>
            </div>
            <div className="mt-auto pt-3">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <div className="icon-text fw-medium">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" className="me-1"><path d="M3.5 0a.5.5 0 0 1 .5.5V1h8V.5a.5.5 0 0 1 1 0V1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h1V.5a.5.5 0 0 1 .5-.5M1 4v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V4z" /></svg>
                        <span>{pkg.duration}</span>
                    </div>
                    <span className="price-tag">{pkg.currency} {pkg.price}</span>
                </div>
                <div className="d-grid">
                    <Button as={Link} to={`/holiday-details/${pkg.packageId}`} variant="primary">View Details</Button>
                </div>
            </div>
        </Card.Body>
    </Card>
);

const TravelTypeResults = () => {
    const { travelTypeName } = useParams(); // Get travel type from the URL parameter
    const [packages, setPackages] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const companyId = DefaultData.DEFAULTCOMPANYID;

    useEffect(() => {
        // Ensure we have the necessary parameters before fetching
        if (!travelTypeName || !companyId) return;

        const getPackagesByType = async () => {
            setIsLoading(true);
            try {
                // Construct the correct API endpoint URL
                const apiUrl = `${config.GETPACKAGEBYTRAVELTYPE}/${companyId}?travelType=${travelTypeName}`;
                
                // Fetch data using the apiService
                const response = await apiService.getAll(apiUrl);
                
                // Assuming apiService.getAll returns the data array directly
                setPackages(response);
            } catch (error) {
                console.error(`Failed to fetch packages for travel type "${travelTypeName}":`, error);
                setPackages([]); // Reset packages on error
            } finally {
                setIsLoading(false);
            }
        };

        getPackagesByType();
    }, [travelTypeName, companyId]); // Re-run effect if travelTypeName or companyId changes

    // Capitalize the first letter of the travel type for a nice title
    const formattedTitle = travelTypeName 
        ? travelTypeName.charAt(0).toUpperCase() + travelTypeName.slice(1) 
        : 'Travel';

    return (
        <div className="main-content">
            <div className="page-content">
                <Container className="my-1 py-1">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <h2 className="section-title mb-0">{formattedTitle} Packages</h2>
                        <Button as={Link} to="/holidays" variant="outline-secondary">
                            <i className="ri-arrow-left-line me-1"></i> Back to Holidays
                        </Button>
                    </div>

                    {isLoading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-2">Loading {formattedTitle} packages...</p>
                        </div>
                    ) : packages && packages.length > 0 ? (
                        <Row xs={1} md={2} lg={3} className="g-4">
                            {packages.map(pkg => (
                                <Col key={pkg.packageId}>
                                    <PackageCard pkg={pkg} />
                                </Col>
                            ))}
                        </Row>
                    ) : (
                        <div className="text-center py-5">
                            <h4>No {formattedTitle} Packages Found</h4>
                            <p>We couldn't find any packages for this category right now. Please check back later.</p>
                            <Button as={Link} to="/holidays">Explore Other Holidays</Button>
                        </div>
                    )}
                </Container>
            </div>
        </div>
    );
};

export default TravelTypeResults;